/**
 * Seven Pairs — structurally different from every other detector in this
 * directory: a genuine Seven Pairs hand (7 distinct kinds, each held twice,
 * `game/WinChecker.ts`'s `isSevenPairs`) has NO valid 4-sets+pair
 * decomposition, so `decomposeConcealedHand` correctly returns `null` for it
 * and `computeBasePoints` degrades gracefully to just the winning bonus (+
 * self-drawn, if applicable) with no per-meld/pair-bonus entries. This
 * detector adds the extra recognition on top, as its own `MatchedPattern[]`
 * (like `computeBasePoints`, not a single match-or-not) — Seven Pairs needs
 * to contribute both an extra base bonus AND a double.
 *
 * Only applies to fully-concealed hands (`context.melds` is always `[]` for
 * a real Seven Pairs hand — you cannot call Pung/Chi/Kong and still form 7
 * pairs), matching `WinChecker.isWinningHand`'s `meldCount === 0` gate for
 * alternate win shapes.
 *
 * **`SEVEN_PAIRS_BASE_BONUS` and `SEVEN_PAIRS_DOUBLES` are a documented
 * convention, not the same cited sloperama.com table `basePoints.ts` uses**
 * — that source didn't give an exact Seven Pairs value. These are
 * commonly-cited round numbers from general Chinese Classical scoring
 * convention, chosen for internal consistency: `SEVEN_PAIRS_BASE_BONUS`
 * stacked on top of `computeBasePoints`'s 20-point winning bonus gives the
 * commonly-cited "30 base points for Seven Pairs," and `SEVEN_PAIRS_DOUBLES`
 * is a modest double value in scale with this table's other doubles. Not
 * asserted as tournament-exact.
 */

import { isSevenPairs } from '../../game/WinChecker.js';
import type { MatchedPattern, ScoringContext } from '../types.js';

const SEVEN_PAIRS_BASE_BONUS = 10;
const SEVEN_PAIRS_DOUBLES = 2;

export function matchSevenPairs(context: ScoringContext): readonly MatchedPattern[] {
  if (context.melds.length > 0) return [];
  if (!isSevenPairs([...context.concealedTiles, context.winningTile])) return [];

  return [
    { name: 'sevenPairs', kind: 'base', value: SEVEN_PAIRS_BASE_BONUS },
    { name: 'sevenPairs', kind: 'double', value: SEVEN_PAIRS_DOUBLES },
  ];
}
